import type { Id, Order, OrderContractElement, Party } from '@hero/schema';

/**
 * Compare two parties via primary `_id` and overlapping `ids[]` entries.
 * A shipper Org and a CRM Client linked back to that org count as "self".
 */
export const partyIdsOverlap = (
  a: Party | undefined,
  b: Party | undefined,
): boolean => {
  if (!a || !b) return false;
  if (a._id?.toString() === b._id?.toString()) return true;
  const aSet = new Set((a.ids ?? []).map(e => `${e.type}:${e.id}`));
  return (b.ids ?? []).some(e => aSet.has(`${e.type}:${e.id}`));
};

/** `true` when the shipper published for themselves (no external CRM client). */
export const isInternalShipment = (order: Order): boolean => {
  if (!order.client) return true;
  return partyIdsOverlap(order.shipper, order.client);
};

/** Inverse of {@link isInternalShipment}. */
export const isOnBehalfOf = (order: Order): boolean => !isInternalShipment(order);

/** Convenience accessor — the publishing org / user `_id`. */
export const getShipperOrgId = (order: Order): Id | undefined => order.shipper?._id;

/**
 * Internal / walk-in orders often have no `contracts[]` leg yet — show the
 * dedicated empty-state copy instead of "not priced" or a zero-price row.
 */
export const shouldShowInternalPriceEmptyState = (
  order: Order,
  viewerLeg?: OrderContractElement,
): boolean => isInternalShipment(order) && !viewerLeg;
